import { useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { ArrowLeft, Plus } from 'lucide-react';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Can } from '../../app/guards';
import { Badge, EmptyState, PageHeader, Pagination, Skeleton, Table, Td, Th } from '../../components/ui';
import { api, qs, type Paginated } from '../../lib/api';
import { fmtDateTime, fmtNumber } from '../../lib/format';
import { useSocketEvent } from '../../lib/socket';
import { dispatchTone, pointLabel, tripTone, type TripRow } from './types';

const TABS = ['', 'SCHEDULED', 'IN_PROGRESS', 'COMPLETED', 'CANCELLED'] as const;

export function TripsPage() {
  const { t, i18n } = useTranslation();
  const nav = useNavigate();
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const status = params.get('status') ?? '';
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const q = useQuery({
    queryKey: ['trips', page, search, status],
    queryFn: () => api<Paginated<TripRow>>(`/trips${qs({ page, q: search, status: status || undefined, sort: status === 'COMPLETED' ? 'startedAt:desc' : 'createdAt:desc' })}`),
  });
  useSocketEvent('trip.updated', useCallback(() => void qc.invalidateQueries({ queryKey: ['trips'] }), [qc]));

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        title={t('nav.trips')}
        subtitle={q.data ? t('common.totalCount', { count: q.data.meta.total }) : undefined}
        actions={
          <Can perm="trips:create">
            <button className="btn-primary" onClick={() => nav('/trips/new')}>
              <Plus size={16} /> {t('trips.new')}
            </button>
          </Can>
        }
      />
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex flex-wrap gap-1 rounded-xl bg-surface-2 p-1" role="tablist">
          {TABS.map((s) => (
            <button
              key={s}
              role="tab"
              aria-selected={status === s}
              className={clsx('min-h-9 rounded-lg px-3 text-sm font-semibold transition-colors', status === s ? 'bg-surface text-brand-ink shadow-sm' : 'text-text-muted hover:text-text')}
              onClick={() => {
                setParams(s ? { status: s } : {});
                setPage(1);
              }}
            >
              {s ? t(`trips.statuses.${s}`) : t('common.all')}
            </button>
          ))}
        </div>
        <input
          className="input max-w-xs"
          placeholder={t('trips.searchPlaceholder')}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          aria-label={t('common.search')}
        />
      </div>
      {q.isLoading ? (
        <Skeleton className="h-72" />
      ) : !q.data?.data.length ? (
        <div className="card">
          <EmptyState title={t('trips.empty')} hint={t('trips.emptyHint')} action={<Can perm="trips:create"><Link className="btn-primary" to="/trips/new"><Plus size={16} /> {t('trips.new')}</Link></Can>} />
        </div>
      ) : (
        <Table>
          <thead>
            <tr>
              <Th>{t('trips.number')}</Th>
              <Th>{t('trips.route')}</Th>
              <Th>{t('trips.vehicle')}</Th>
              <Th>{t('trips.driver')}</Th>
              <Th>{t('trips.customer')}</Th>
              <Th>{t('trips.when')}</Th>
              <Th>{t('trips.distance')}</Th>
              <Th>{t('trips.status')}</Th>
            </tr>
          </thead>
          <tbody>
            {q.data.data.map((tr) => (
              <tr key={tr.id} className="hover:bg-surface-hover">
                <Td>
                  <Link to={`/trips/${tr.id}`} className="font-bold text-brand-ink hover:underline" dir="ltr">
                    {tr.number}
                  </Link>
                  {tr.shipmentOrderNo ? <div className="text-xs text-text-muted">{t('trips.shipmentOrderNo')}: {tr.shipmentOrderNo}</div> : null}
                </Td>
                <Td>
                  <div className="flex items-center gap-1 text-sm">
                    <span className="max-w-40 truncate">{pointLabel(tr, 'origin') ?? '—'}</span>
                    <ArrowLeft size={14} className="shrink-0 text-text-faint rtl:rotate-0 ltr:rotate-180" aria-hidden />
                    <span className="max-w-40 truncate">{pointLabel(tr, 'dest') ?? '—'}</span>
                  </div>
                </Td>
                <Td>{tr.vehicle ? <Link to={`/vehicles/${tr.vehicle.id}`} className="hover:underline">{tr.vehicle.plateAr}</Link> : <span className="text-text-faint">—</span>}</Td>
                <Td>{tr.driver ? <Link to={`/drivers/${tr.driver.id}`} className="hover:underline">{tr.driver.fullName}</Link> : <span className="text-text-faint">—</span>}</Td>
                <Td>{tr.customer?.name ?? <span className="text-text-faint">—</span>}</Td>
                <Td className="text-text-muted">{fmtDateTime(tr.startedAt ?? tr.scheduledAt ?? tr.createdAt, i18n.language)}</Td>
                <Td>{tr.distanceKm != null ? `${fmtNumber(tr.distanceKm, i18n.language, 1)} ${t('units.km')}` : '—'}</Td>
                <Td>
                  <div className="flex flex-wrap gap-1">
                    <Badge tone={tripTone(tr.status)} dot>{t(`trips.statuses.${tr.status}`)}</Badge>
                    {tr.dispatchStatus !== 'NONE' && (tr.status === 'DRAFT' || tr.status === 'SCHEDULED') ? <Badge tone={dispatchTone(tr.dispatchStatus)}>{t(`trips.dispatch.${tr.dispatchStatus}`)}</Badge> : null}
                  </div>
                </Td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={8}>
                <Pagination page={q.data.meta.page} pageSize={q.data.meta.pageSize} total={q.data.meta.total} onPage={setPage} />
              </td>
            </tr>
          </tfoot>
        </Table>
      )}
    </div>
  );
}
